import type { ThunkAction } from '@reduxjs/toolkit';
import { CarsService } from 'store/services/cars.service';
import type { RootState } from 'store/index';
import type { Dayjs } from 'dayjs';
import type { CAR_FILTERS_TYPE } from 'components/Admin/organisms/CarAvailability/molecules/CarAvailabilitiesHeader/constants';
import type {
  RemoveCarFilesQueries,
  UpdateCarDocumentQueries,
} from 'store/endpoints/types';
import { endpoints } from 'store/endpoints';
import { carsActions, carsSelectors } from './config';
import type {
  Car,
  CarRequest,
  GetCarsParams,
  SearchCarsParam,
  CarOptionType,
  CarCharacteristicsType,
  CarStatus,
} from './types';

export const searchCars =
  (
    params: Partial<SearchCarsParam>,
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      dispatch(carsActions.setCarsPendingStatus(true));
      const response = await CarsService.searchCars(params);
      dispatch(carsActions.setSearchItems(response));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(carsActions.setCarsPendingStatus(false));
    }
  };
export const getCars =
  (
    params?: Partial<GetCarsParams>,
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      dispatch(carsActions.setCarsPendingStatus(true));

      const response = await CarsService.getCars(params);
      dispatch(carsActions.setCars(response.rows));
      dispatch(carsActions.setTotalCount(response.count));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(carsActions.setCarsPendingStatus(false));
    }
  };

export const createNewCar =
  (cb: () => void): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch, getState) => {
    try {
      dispatch(carsActions.setCarsPendingStatus(true));
      const newCar = carsSelectors.selectNewCar(getState());
      const eventSource = new EventSource(
        endpoints.cars.getCarCreationProgress,
      );
      eventSource.onopen = () => {
        CarsService.createNewCar(newCar as CarRequest).then((response) => {
          eventSource.close();
          dispatch(carsActions.resetNewCarState());
          dispatch(carsActions.addNewCar(response));
          dispatch(carsActions.setCarCreationProgress(null));
          cb();
        });
      };
      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);
        const progress = data.progress;
        dispatch(carsActions.setCarCreationProgress(progress));
      };
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(carsActions.setCarsPendingStatus(false));
    }
  };

export const addAvailabilityDates =
  ({
    body,
    carId,
  }: {
    body: Array<string>;
    carId: string;
  }): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      const response = await CarsService.addAvailabilityDates(body, carId);
      dispatch(carsActions.updateCar(response));
    } catch (err: any) {
      throw new Error(err);
    }
  };

export const removeAvailableDate =
  ({
    body,
    carId,
  }: {
    body: Array<string>;
    carId: string;
  }): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      const response = await CarsService.removeAvailableDate(body, carId);
      dispatch(carsActions.updateCar(response));
    } catch (err: any) {
      throw new Error(err);
    }
  };

export const removeSelectedCars =
  (
    selectedCarsIds: Array<string>,
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      dispatch(carsActions.setCarsPendingStatus(true));
      await CarsService.removeCars(selectedCarsIds);
      dispatch(carsActions.removeCars(selectedCarsIds));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(carsActions.setCarsPendingStatus(false));
    }
  };

export const getCarById =
  (id: string): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      dispatch(carsActions.setCarsPendingStatus(true));
      const response = await CarsService.getCarById(id);
      dispatch(
        carsActions.setSelectedCar({
          ...response,
          characteristics: response.characteristics.map((item) => ({
            ...item,
            type: item.type.substring(
              item.type.lastIndexOf('.') + 1,
            ) as CarCharacteristicsType,
          })),
          options: response.options.map((item) => ({
            ...item,
            type: item.type.substring(
              item.type.lastIndexOf('.') + 1,
            ) as CarOptionType,
          })),
        }),
      );
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(carsActions.setCarsPendingStatus(false));
    }
  };

export const editCarThunk =
  (
    body: Partial<Car>,
    carId: string,
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      dispatch(carsActions.setCarsPendingStatus(true));
      const response = await CarsService.editCar(body, carId);
      dispatch(carsActions.setSelectedCar(null));
      dispatch(carsActions.setSelectedCarChangedKeys([]));
      dispatch(carsActions.updateCar(response));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(carsActions.setCarsPendingStatus(false));
    }
  };

export const getCarsByAvailability =
  (
    availabilityType: CAR_FILTERS_TYPE,
    dates: Array<Dayjs>,
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      dispatch(carsActions.setCarsPendingStatus(true));
      const response = await CarsService.getCarsByAvailability(
        availabilityType,
        dates,
      );
      dispatch(carsActions.setCars(response));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(carsActions.setCarsPendingStatus(false));
    }
  };

export const updateCarsThunk =
  (
    selectedCars: Array<string>,
    status: CarStatus,
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      dispatch(carsActions.setCarsPendingStatus(true));
      const response = await CarsService.updateCars(selectedCars, status);
      dispatch(carsActions.updateCars(response));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(carsActions.setCarsPendingStatus(false));
    }
  };

export const duplicateCar =
  (id: string): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      dispatch(carsActions.setCarsPendingStatus(true));
      const response = await CarsService.duplicateCar(id);
      dispatch(carsActions.addNewCar(response));
      dispatch(carsActions.setSelectedCar(response));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(carsActions.setCarsPendingStatus(false));
    }
  };

export const removeCarFile =
  (params: RemoveCarFilesQueries): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      dispatch(carsActions.setFilesBeingDeleted(params.fileId));

      const updatedCar = await CarsService.removeCarFile(params);
      dispatch(carsActions.updateCarUploadedFiles(updatedCar.uploadedFiles));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(carsActions.setFilesBeingDeleted(params.fileId));
    }
  };

export const removeCarInitialImageThunk =
  (imageId: string): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      await CarsService.removeCarInitialPhoto(imageId);
      dispatch(carsActions.removeInitialCarImage(imageId));
    } catch (err: any) {
      throw new Error(err);
    }
  };

export const updateCarInitialPhoto =
  (file: File): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch, getState) => {
    try {
      const selectedCar = carsSelectors.selectSelectedCar(getState());
      const response = await CarsService.updateCarInitialPhoto(
        file,
        selectedCar.id,
      );
      dispatch(
        carsActions.setSelectedCar({
          ...selectedCar,
          uploadedFiles: selectedCar.uploadedFiles.concat(response),
        }),
      );

      return response;
    } catch (err: any) {
      throw new Error(err);
    }
  };

export const updateCarDocumentThunk =
  (
    params: UpdateCarDocumentQueries,
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch, getState) => {
    try {
      dispatch(
        carsActions.setCarDocumentPendingStatus({
          key: params.fileKey,
          status: true,
        }),
      );
      const selectedCar = carsSelectors.selectSelectedCar(getState());
      const response = await CarsService.updateCarDocument({
        ...params,
        id: selectedCar.id,
      });

      dispatch(
        carsActions.setSelectedCar({
          ...selectedCar,
          [params.fileKey]: response[params.fileKey],
        }),
      );
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(
        carsActions.setCarDocumentPendingStatus({
          key: params.fileKey,
          status: false,
        }),
      );
    }
  };

export const updateCarFileThunk =
  (file: File): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch, getState) => {
    try {
      const selectedCar = carsSelectors.selectSelectedCar(getState());
      const car = await CarsService.updateCarFile({
        file,
        carId: selectedCar.id,
      });
      dispatch(carsActions.updateCarUploadedFiles(car.uploadedFiles));
    } catch (err: any) {
      throw new Error(err);
    }
  };

export const updateCarCoverImage =
  (file: File): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch, getState) => {
    try {
      const selectedCar = carsSelectors.selectSelectedCar(getState());
      const car = await CarsService.updateCoverImage({
        file,
        carId: selectedCar.id,
      });
      dispatch(carsActions.setSelectedCar(car));
    } catch (err: any) {
      throw new Error(err);
    }
  };

export const updateCarMedia =
  (
    images: Array<{ id: string; file: File }>,
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch, getState) => {
    try {
      const car = carsSelectors.selectSelectedCar(getState());
      if (!car.id) {
        return;
      }
      const media = images.map((item) => item.file);
      const result = await CarsService.updateCarMedia(
        media,
        car.id,
        (progress) => {
          dispatch(carsActions.setMediaUploadProgress(progress));
        },
      );
      const newFiles = (car.uploadedFiles || []).concat(result);
      const updatedCar = {
        ...car,
        uploadedFiles: newFiles,
      };
      dispatch(carsActions.updateCar(updatedCar));
      dispatch(carsActions.updateCarUploadedFiles(newFiles));
    } catch (err: any) {
      console.error(err);
    }
  };
